import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    LinearProgress
} from '@mui/material';
import {
    Receipt,
    Close,
    Launch,
    Info,
    Print,
    PrintDisabled,
    Warning
} from '@mui/icons-material';

const InvoicesModal = ({ open, onClose, invoices, loading = false, sku, error = null, onNavigateToSku, onPrintInvoice, onPrintAll, bridgeConnected, printingState }) => {
    const handleClose = () => {
        onClose();
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusChip = (status) => {
        const statusConfig = {
            printed: { color: 'success', label: 'Impresso' },
            pending: { color: 'warning', label: 'Pendente' },
            error: { color: 'error', label: 'Erro' }
        };
        
        const config = statusConfig[status] || { color: 'default', label: status };
        
        return (
            <Chip
                label={config.label}
                color={config.color}
                size="small"
                sx={{ fontWeight: 500 }}
            />
        );
    };

    const getCurrentItem = (invoice) => {
        if (!invoice.content?.items_list || !Array.isArray(invoice.content.items_list)) {
            return null;
        }
        return invoice.content.items_list.find(item => item.item_sku === sku) || invoice.content.items_list[0];
    };

    const getOtherItems = (invoice) => {
        if (!invoice.content?.items_list || !Array.isArray(invoice.content.items_list)) {
            return [];
        }
        return invoice.content.items_list.filter(item => item.item_sku !== sku);
    };

    const getEligibleForBatchPrint = () => {
        if (!invoices || invoices.length === 0) return [];
        return invoices.filter(invoice => {
            const currentItem = getCurrentItem(invoice);
            const otherItems = getOtherItems(invoice);
            // Inclui apenas etiquetas que têm o SKU específico e não têm outros itens
            return currentItem && otherItems.length === 0;
        });
    };

    const getMultiItemInvoices = () => {
        if (!invoices || invoices.length === 0) return [];
        return invoices.filter(invoice => {
            const otherItems = getOtherItems(invoice);
            return otherItems.length > 0;
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    minHeight: '600px'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: 1.5,
                pb: 2,
                borderBottom: '1px solid #f0f0f0'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Receipt sx={{ color: '#FF7322' }} />
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                            Etiquetas de Transporte - {sku}
                        </Typography>
                        {!loading && !error && (
                            <Box>
                                <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                                    {invoices?.length || 0} etiqueta(s) encontrada(s)
                                </Typography>
                                {!loading && !error && invoices && invoices.length > 0 && getMultiItemInvoices().length > 0 && (
                                    <Box sx={{ 
                                        mt: 1, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 0.5,
                                        bgcolor: 'rgba(255, 152, 0, 0.08)',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        border: '1px solid rgba(255, 152, 0, 0.2)'
                                    }}>
                                        <Warning sx={{ color: '#ff9800', fontSize: '0.9rem' }} />
                                        <Typography variant="caption" sx={{ color: '#e65100', fontSize: '0.7rem', fontWeight: 500 }}>
                                            {getMultiItemInvoices().length} etiqueta(s) com múltiplos itens serão excluídas da impressão em lote
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ py: 3, px: 3 }}>
                {loading && (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 6
                    }}>
                        <CircularProgress size={40} sx={{ color: '#FF7322', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Carregando etiquetas de transporte...
                        </Typography>
                    </Box>
                )}

                {error && !loading && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && !error && (!invoices || invoices.length === 0) && (
                    <Alert severity="info" sx={{ textAlign: 'center' }}>
                        Nenhuma etiqueta de transporte encontrada para este SKU no período selecionado.
                    </Alert>
                )}

                {!loading && !error && invoices && invoices.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
                        {invoices.map((invoice, index) => {
                            const currentItem = getCurrentItem(invoice);
                            const otherItems = getOtherItems(invoice);
                            const hasMultipleItems = otherItems.length > 0;
                            
                            return (
                                <Box
                                    key={invoice._id || index}
                                    sx={{
                                        border: hasMultipleItems ? '2px solid rgba(33, 150, 243, 0.2)' : '1px solid rgba(0,0,0,0.08)',
                                        borderRadius: 2,
                                        p: 3,
                                        backgroundColor: hasMultipleItems ? 'rgba(33, 150, 243, 0.02)' : '#fafafa',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        '&:hover': {
                                            backgroundColor: hasMultipleItems ? 'rgba(33, 150, 243, 0.05)' : '#f5f5f5',
                                            borderColor: '#FF7322',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }
                                    }}
                                >
                                    {hasMultipleItems && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}
                                        >
                                            <Tooltip title={`Esta etiqueta contém ${otherItems.length + 1} itens diferentes`}>
                                                <Info sx={{ color: '#1976d2', fontSize: 18 }} />
                                            </Tooltip>
                                        </Box>
                                    )}

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, pr: hasMultipleItems ? 4 : 0 }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: 600, 
                                                    color: '#333',
                                                    mb: 1
                                                }}
                                            >
                                                NF-e: {invoice.content?.invoice_number || 'N/A'}
                                            </Typography>
                                            
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                {getStatusChip(invoice.printed ? 'printed' : 'pending')}
                                                {invoice.channel && (
                                                    <Chip
                                                        label={invoice.channel.replace('_', ' ')}
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{ 
                                                            borderColor: '#FF7322', 
                                                            color: '#FF7322',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                )}
                                                {hasMultipleItems && (
                                                    <Chip
                                                        label={`${otherItems.length + 1} itens`}
                                                        size="small"
                                                        sx={{ 
                                                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                            color: '#1976d2',
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                        
                                        <Box sx={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    Valor Total
                                                </Typography>
                                                <Typography 
                                                    variant="h6" 
                                                    sx={{ 
                                                        fontWeight: 600, 
                                                        color: '#4caf50'
                                                    }}
                                                >
                                                    {formatCurrency(invoice.content?.invoice_amount)}
                                                </Typography>
                                            </Box>
                                            
                                            <Tooltip title={!bridgeConnected ? 'Bridge desconectado - impossível imprimir' : 'Imprimir esta etiqueta de transporte'}>
                                                <span>
                                                    <IconButton
                                                        onClick={() => onPrintInvoice && onPrintInvoice(invoice)}
                                                        disabled={!bridgeConnected || !onPrintInvoice || (printingState?.isProcessing && printingState?.currentInvoice === invoice._id)}
                                                        sx={{
                                                            width: 42,
                                                            height: 42,
                                                            background: bridgeConnected 
                                                                ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' 
                                                                : 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                                                            color: bridgeConnected ? '#ffffff' : '#757575',
                                                            border: '2px solid',
                                                            borderColor: bridgeConnected ? 'rgba(25, 118, 210, 0.3)' : 'rgba(189, 189, 189, 0.3)',
                                                            boxShadow: bridgeConnected 
                                                                ? '0 4px 12px rgba(25, 118, 210, 0.25), 0 2px 4px rgba(25, 118, 210, 0.12)' 
                                                                : '0 2px 4px rgba(158, 158, 158, 0.2)',
                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                            '&:hover': {
                                                                background: bridgeConnected 
                                                                    ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)' 
                                                                    : 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                                                                transform: bridgeConnected ? 'translateY(-2px) scale(1.05)' : 'none',
                                                                boxShadow: bridgeConnected 
                                                                    ? '0 8px 20px rgba(25, 118, 210, 0.3), 0 4px 8px rgba(25, 118, 210, 0.15)' 
                                                                    : '0 2px 4px rgba(158, 158, 158, 0.2)',
                                                                borderColor: bridgeConnected ? 'rgba(25, 118, 210, 0.5)' : 'rgba(189, 189, 189, 0.3)'
                                                            },
                                                            '&:active': {
                                                                transform: bridgeConnected ? 'translateY(0px) scale(0.98)' : 'none'
                                                            },
                                                            '&:disabled': {
                                                                background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
                                                                color: '#bdbdbd',
                                                                borderColor: 'rgba(189, 189, 189, 0.2)',
                                                                boxShadow: 'none',
                                                                cursor: 'not-allowed'
                                                            }
                                                        }}
                                                    >
                                                        {printingState?.isProcessing && printingState?.currentInvoice === invoice._id ? (
                                                            <CircularProgress size={20} sx={{ color: 'currentColor' }} />
                                                        ) : bridgeConnected ? (
                                                            <Print fontSize="small" />
                                                        ) : (
                                                            <PrintDisabled fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 2 }} />

                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap',
                                        gap: 3,
                                        '& > div': { minWidth: '200px' }
                                    }}>
                                        <Box sx={{ flex: 1, minWidth: '300px' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Item Principal
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                                {currentItem?.item_name || invoice.content?.item_title || 'N/A'}
                                            </Typography>
                                            {currentItem && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    <Chip
                                                        label={`Qtd: ${currentItem.quantity}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ borderColor: '#4caf50', color: '#4caf50' }}
                                                    />
                                                    <Chip
                                                        label={`SKU: ${currentItem.item_sku}`}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ borderColor: '#FF7322', color: '#FF7322' }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>

                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Data de Criação
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                {formatDate(invoice.createdAt)}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ minWidth: '250px' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                Chave de Acesso
                                            </Typography>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    fontWeight: 500,
                                                    fontFamily: 'monospace',
                                                    backgroundColor: 'rgba(255, 115, 34, 0.1)',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    display: 'inline-block',
                                                    fontSize: '0.8rem',
                                                    wordBreak: 'break-all'
                                                }}
                                            >
                                                {invoice.chave_acesso || 'N/A'}
                                            </Typography>
                                        </Box>

                                        {invoice.shipping_id && (
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    ID do Envio
                                                </Typography>
                                                <Typography 
                                                    variant="body1" 
                                                    sx={{ 
                                                        fontWeight: 500,
                                                        fontFamily: 'monospace',
                                                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        display: 'inline-block'
                                                    }}
                                                >
                                                    {invoice.shipping_id}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    {hasMultipleItems && (
                                        <>
                                            <Divider sx={{ my: 2 }} />
                                            <Box sx={{ 
                                                backgroundColor: 'rgba(33, 150, 243, 0.03)', 
                                                borderRadius: 1, 
                                                p: 2,
                                                border: '1px solid rgba(33, 150, 243, 0.1)'
                                            }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                                        Outros itens nesta etiqueta ({otherItems.length})
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {otherItems.map((item, itemIndex) => (
                                                        <Box 
                                                            key={itemIndex}
                                                            sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                gap: 1,
                                                                backgroundColor: '#fff',
                                                                border: '1px solid rgba(33, 150, 243, 0.2)',
                                                                borderRadius: 1,
                                                                p: 1
                                                            }}
                                                        >
                                                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                                <strong>SKU:</strong> {item.item_sku} | <strong>Qtd:</strong> {item.quantity}
                                                            </Typography>
                                                            {onNavigateToSku && (
                                                                <Tooltip title={`Ver etiquetas do SKU ${item.item_sku}`}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => onNavigateToSku(item.item_sku)}
                                                                        sx={{ 
                                                                            ml: 1,
                                                                            backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(33, 150, 243, 0.2)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Launch fontSize="small" sx={{ color: '#1976d2' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>

            {printingState?.isProcessing && (
                <Box 
                    sx={{ 
                        px: 3, 
                        pb: 2, 
                        bgcolor: 'rgba(255, 115, 34, 0.02)', 
                        borderTop: '1px solid rgba(255, 115, 34, 0.1)',
                        borderBottom: '1px solid rgba(255, 115, 34, 0.1)'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #FF7322 0%, #ff9800 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pulse 2s infinite'
                            }}
                        >
                            <Print sx={{ color: 'white', fontSize: '1rem' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    fontWeight: 600, 
                                    color: '#FF7322',
                                    mb: 0.5
                                }}
                            >
                                Processando impressão de etiquetas em lote
                            </Typography>
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: '#666',
                                    fontSize: '0.75rem'
                                }}
                            >
                                Imprimindo etiquetas: {printingState.processedCount + 1} de {printingState.totalCount} • {Math.round(((printingState.processedCount) / printingState.totalCount) * 100)}% concluído
                            </Typography>
                        </Box>
                        <Chip
                            label={`${printingState.processedCount}/${printingState.totalCount}`}
                            size="small"
                            sx={{
                                bgcolor: 'rgba(255, 115, 34, 0.1)',
                                color: '#FF7322',
                                fontWeight: 600,
                                fontSize: '0.7rem'
                            }}
                        />
                    </Box>
                    
                    <LinearProgress 
                        variant="determinate" 
                        value={((printingState.processedCount) / printingState.totalCount) * 100}
                        sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: 'rgba(255, 115, 34, 0.1)',
                            '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #FF7322 0%, #ff9800 100%)',
                                borderRadius: 4,
                                transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                            }
                        }}
                    />
                    
                    <style jsx global>{`
                        @keyframes pulse {
                            0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 115, 34, 0.4); }
                            50% { transform: scale(1.05); box-shadow: 0 0 0 8px rgba(255, 115, 34, 0.1); }
                            100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(255, 115, 34, 0.0); }
                        }
                    `}</style>
                </Box>
            )}

            <DialogActions sx={{ p: 3, pt: 1, gap: 1, borderTop: '1px solid #f0f0f0' }}>
                <Button
                    onClick={handleClose}
                    startIcon={<Close />}
                    disabled={printingState?.isProcessing}
                    variant="outlined"
                    sx={{
                        color: '#6c757d',
                        borderColor: 'rgba(108, 117, 125, 0.3)',
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        px: 2.5,
                        py: 1,
                        borderRadius: 1.5,
                        textTransform: 'none',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: 'rgba(108, 117, 125, 0.08)',
                            borderColor: 'rgba(108, 117, 125, 0.5)',
                            transform: 'translateY(-1px)'
                        },
                        '&:disabled': {
                            color: '#bdbdbd',
                            borderColor: 'rgba(189, 189, 189, 0.3)',
                            cursor: 'not-allowed'
                        },
                        '& .MuiButton-startIcon': {
                            marginRight: 0.5
                        }
                    }}
                >
                    Fechar
                </Button>

                {!loading && !error && invoices && invoices.length > 0 && (
                    <Button
                        onClick={onPrintAll}
                        startIcon={printingState?.isProcessing ? <CircularProgress size={16} sx={{ color: 'currentColor' }} /> : <Print />}
                        disabled={!bridgeConnected || !onPrintAll || printingState?.isProcessing || getEligibleForBatchPrint().length === 0}
                        variant="contained"
                        sx={{
                            background: bridgeConnected && getEligibleForBatchPrint().length > 0
                                ? 'linear-gradient(135deg, #FF7322 0%, #ff9800 100%)' 
                                : 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                            color: bridgeConnected && getEligibleForBatchPrint().length > 0 ? "#ffffff" : "#757575",
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 3,
                            py: 1,
                            borderRadius: 1.5,
                            border: '2px solid',
                            borderColor: bridgeConnected && getEligibleForBatchPrint().length > 0 ? 'rgba(255, 115, 34, 0.3)' : 'rgba(189, 189, 189, 0.3)',
                            boxShadow: bridgeConnected && getEligibleForBatchPrint().length > 0
                                ? '0 4px 12px rgba(255, 115, 34, 0.25), 0 2px 6px rgba(255, 115, 34, 0.12)' 
                                : '0 2px 4px rgba(158, 158, 158, 0.2)',
                            textTransform: 'none',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': { 
                                background: bridgeConnected && getEligibleForBatchPrint().length > 0
                                    ? 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)' 
                                    : 'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                                transform: bridgeConnected && getEligibleForBatchPrint().length > 0 ? 'translateY(-1px)' : 'none',
                                boxShadow: bridgeConnected && getEligibleForBatchPrint().length > 0
                                    ? '0 6px 16px rgba(255, 115, 34, 0.35), 0 3px 8px rgba(255, 115, 34, 0.15)' 
                                    : '0 2px 4px rgba(158, 158, 158, 0.2)',
                                borderColor: bridgeConnected && getEligibleForBatchPrint().length > 0 ? 'rgba(255, 115, 34, 0.5)' : 'rgba(189, 189, 189, 0.3)'
                            },
                            '&:active': {
                                transform: bridgeConnected && getEligibleForBatchPrint().length > 0 ? 'translateY(0px)' : 'none'
                            },
                            '&:disabled': {
                                background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
                                color: "#bdbdbd",
                                borderColor: 'rgba(189, 189, 189, 0.2)',
                                boxShadow: 'none',
                                cursor: 'not-allowed'
                            },
                            '& .MuiButton-startIcon': {
                                marginRight: 1
                            }
                        }}
                    >
                        {printingState?.isProcessing ? 'Imprimindo Etiquetas...' : `Imprimir Etiquetas Elegíveis (${getEligibleForBatchPrint().length})`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default InvoicesModal;